import { useCallback, useEffect, useState } from "react";
import { useTimer } from "react-use-precision-timer";

import Stack from "@mui/material/Stack";
import useTheme from "@mui/material/styles/useTheme";

import { differenceInMinutes, differenceInSeconds } from "date-fns";

import { AppointmentsProvider } from "features/scheduling/hooks/use-appointments.hook";

import { DIALER_CALL_STATE_ON_HOLD } from "../constants";
import useSunoDialer from "../use-suno-dialer.hook";
import ActionButtonsBlock from "./components/action-buttons-block.component";
import HeaderBlock from "./components/header-block.component";
import InfoBlock from "./components/info-block.component";
import NotesBlock from "./components/notes-block.component";
import TagsBlock from "./components/tags-block.component";
import TitleBlock from "./components/title-block.component";

export default function ActiveCallWidget() {
  const theme = useTheme();
  const { activeCallDetails, isExpanded } = useSunoDialer();
  const [elapsedTime, setElapsedTime] = useState("00:00");

  const timerCallback = useCallback(() => {
    const now = new Date();
    const pickedUpAt = new Date(activeCallDetails?.pickedUpAt || "");

    const minutes =
      differenceInMinutes(now, pickedUpAt, {
        roundingMethod: "floor"
      }) || 0;

    const seconds =
      Math.floor(
        differenceInSeconds(now, pickedUpAt, {
          roundingMethod: "round"
        }) % 60
      ) || 0;

    setElapsedTime(
      `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    );
  }, [activeCallDetails?.pickedUpAt]);

  const timer = useTimer({ delay: 1000 }, timerCallback);

  useEffect(() => {
    if (!timer.isStarted()) timer.start();
  }, [timer]);

  const isOnHold = activeCallDetails?.state === DIALER_CALL_STATE_ON_HOLD;

  return (
    <AppointmentsProvider>
      <Stack
        sx={{
          background: isOnHold
            ? "hsla(180, 1%, 15%, 0.8)"
            : "hsla(154, 45%, 40%, 0.6)",
          borderRadius: "12px",
          boxSizing: "border-box",
          boxShadow: theme.shadows[3],
          cursor: "pointer"
        }}
      >
        <HeaderBlock elapsedTime={elapsedTime} />
        {isExpanded && (
          <>
            <TagsBlock />
            <TitleBlock />
            <NotesBlock />
            <InfoBlock />
            <ActionButtonsBlock />
          </>
        )}
      </Stack>
    </AppointmentsProvider>
  );
}
